import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import api, { API_PATHS, AuthService } from "../service/api";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { initiateNexusLandingDataExtraction } from "../store/ganttChart/action";
import {
  setActiveNexusFilter,
  setSelectedNexusFilterDate,
} from "../store/nexusFilters";
import { setPlanViewStateLoading } from "../store/realTimeUpdates";
import {
  createPlanViewStateKey,
  serializeKey,
} from "../store/realTimeUpdates/stateKeyUtils";
import { decodeJobId } from "../store/realTimeUpdates/helpers";
import { PlanViewStateEntry } from "../store/realTimeUpdates";
import AppInitializationService from "../service/AppInitializationService";
import labels from "../utils/labels";
import { AxiosResponse } from "axios";
import { Spinner } from "../components/UI/Spinner";
import AlreadyExistsWarning from "../components/nexusLanding/AlreadyExistsWarning";
import RunInProgressDialog from "../components/nexusLanding/RunInProgressDialog";
import {
  NexusFilter,
  NexusFilterResponse,
  RunInProgress,
} from "../types/nexusLanding";

const FINISHED_JOB_STATUSES = ["Completed", "Failed", "Aborted"];

/**
 * The backend reports the run's stage once, when the page loads. The websocket messages move it on from there:
 * a job for the followed run means it optimizes, a finished job means it is done. A superseded run hands the
 * cell to a newer run (activeJobRef), which starts over from fetching.
 */
export const followRun = (
  run: RunInProgress,
  pondooEntry?: PlanViewStateEntry,
): RunInProgress["stage"] | "DONE" => {
  const jobRef = pondooEntry?.activeJobRef ?? run.jobRef;
  const job = pondooEntry?.job?.jobRef === jobRef ? pondooEntry.job : undefined;
  if (job && FINISHED_JOB_STATUSES.includes(job.jobStatus)) return "DONE";
  if (job) return "OPTIMIZING";
  return jobRef === run.jobRef ? run.stage : "FETCHING";
};

const NexusLandingPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Prevent multiple executions of the OAuth flow
  const hasInitialized = useRef(false);

  const [pageDisplayState, setPageDisplayState] = useState<
    "Spinner" | "Error" | "AlreadyExistsWarning" | "RunInProgress"
  >("Spinner");

  // A ref, not only state: two clicks in the same tick both see the stale state value
  const hasStartedExtraction = useRef(false);
  const [isStarting, setIsStarting] = useState(false);

  const [pendingNexusFilterResponse, setPendingNexusFilterResponse] = useState<
    AxiosResponse<NexusFilterResponse> | undefined
  >();

  const runInProgress = pendingNexusFilterResponse?.data.runInProgress;
  const runPondooEntry = useAppSelector((state) => {
    if (!runInProgress) return undefined;
    const { filter, date } = decodeJobId(runInProgress.jobRef);
    return state.realTimeUpdateSlice[
      serializeKey(
        createPlanViewStateKey("PondooSolution", parseInt(filter), date),
      )
    ];
  });
  const liveRunStage =
    runInProgress && followRun(runInProgress, runPondooEntry);

  // When the followed run ends, the plan exists, so offer the usual overwrite choice
  useEffect(() => {
    if (pageDisplayState === "RunInProgress" && liveRunStage === "DONE")
      setPageDisplayState("AlreadyExistsWarning");
  }, [pageDisplayState, liveRunStage]);

  const appInitializationService = useMemo(() => {
    return new AppInitializationService(dispatch);
  }, [dispatch]);

  const hasPassedAuthChallenge = (): boolean => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.has("code");
  };

  const handleInitialLanding = async () => {
    const searchParams = new URLSearchParams(window.location.search);

    // Check if we have URL params (initial landing before auth)
    const filterId = searchParams.get("filterId");
    const date = searchParams.get("date");
    const filterName = searchParams.get("filterName");
    const resourceIds = searchParams.get("resourceIds");

    if (filterId && date && filterName) {
      // Initial landing - perform OAuth flow
      await AuthService.performLogin(filterId, date, filterName, resourceIds);
      return;
    }

    // Otherwise, this is post-auth redirect - state param should exist
    const stateData = decodeStateParam();

    if (
      !stateData ||
      !stateData.filterId ||
      !stateData.date ||
      !stateData.filterName
    ) {
      console.error("Missing required parameters after auth redirect");
      navigate("/");
      return;
    }

    // stateData is available for use in subsequent steps
  };

  const getSanitizedStateDate = (): string | undefined =>
    decodeStateParam()?.date?.split("T")[0];

  const sanitizeLocalDate = (date: string): string => date.split("T")[0];

  /**
   * Decodes the "state" parameter from the URL.
   * @returns {object|null} The decoded state object or null if decoding fails.
   */
  const decodeStateParam = (): {
    date: string | null;
    filterId: string | null;
    filterName: string | null;
    resourceIds: string | null;
  } | null => {
    const searchParams = new URLSearchParams(window.location.search);
    const stateParam = searchParams.get("state");

    if (!stateParam) {
      return null;
    }

    try {
      const decoded = atob(stateParam);
      return JSON.parse(decoded);
    } catch (error) {
      console.error("Failed to decode state parameter:", error);
      return null;
    }
  };

  /**
   * Seed the plan view state as "loading" before navigating to the Gantt chart.
   * Without this, useSolutionPlanView falls back to a "missing" default for the
   * new filter/date key until the first websocket message arrives, which briefly
   * flashes the "no plan" message in between the two spinner states.
   * The keys use Pondoo's filter id (the URL's and the jobRef's), not the Nexus filter id.
   */
  const seedPlanViewStateLoading = (pondooFilterId: number, date: string) => {
    dispatch(
      setPlanViewStateLoading(
        createPlanViewStateKey("NexusSolution", pondooFilterId, date),
      ),
    );
    dispatch(
      setPlanViewStateLoading(
        createPlanViewStateKey("PondooSolution", pondooFilterId, date),
      ),
    );
  };

  const navigateToCell = (pondooFilterId: number, date: string) => {
    navigate({
      pathname: "/",
      search: createSearchParams({
        filter: pondooFilterId.toString(),
        date,
      }).toString(),
    });
  };

  const startNexusLandingDataExtraction = async (
    nexusFilterResponse: AxiosResponse<NexusFilterResponse>,
  ) => {
    if (hasStartedExtraction.current) return;
    hasStartedExtraction.current = true;
    setIsStarting(true);

    // Decode state parameter to get the original query data
    const stateData = decodeStateParam();

    if (!stateData) {
      console.error("Cannot extract data without state parameters");
      hasStartedExtraction.current = false;
      setIsStarting(false);
      setPageDisplayState("Error");
      return;
    }

    const { filterId, date, filterName, resourceIds } = stateData;

    const sanitizedFilterName = decodeURIComponent(filterName ?? "");
    const sanitizedDate = date!.split("T")[0];
    const sanitizedResourceIds = decodeURIComponent(resourceIds ?? "");

    // Set active filter and date before redirecting
    dispatch(
      setActiveNexusFilter({
        ...nexusFilterResponse.data.filter,
        filterName: sanitizedFilterName,
      }),
    );
    dispatch(setSelectedNexusFilterDate(sanitizedDate));
    // We have a filter, queue the data extraction
    const data = nexusFilterResponse.data.filter as NexusFilter;

    dispatch(
      initiateNexusLandingDataExtraction({
        filterId: parseInt(filterId!),
        date: sanitizeLocalDate(date!),
        pondooFilterId: data.id,
        filterName: sanitizedFilterName,
        resourceIds: sanitizedResourceIds,
      }),
    );

    seedPlanViewStateLoading(data.id, sanitizedDate);

    setTimeout(() => navigateToCell(data.id, sanitizedDate), 2000);
  };

  /**
   * Performs an initial hand-shake with the Pondoo application to ensure we have a filter ready. Also checks
   * if the filter already has data on the given planning date - if so, the response is stashed and the user is informed.
   */
  const initiateNexusLandingChecks = async () => {
    // Decode state parameter to get the original query data
    const stateData = decodeStateParam();

    if (!stateData) {
      console.error("No state parameter found after auth redirect");
      setPageDisplayState("Error");
      return;
    }

    const { filterId, date, filterName } = stateData;

    const sanitizedFilterName = decodeURIComponent(filterName ?? "");
    const sanitizedDate = date!.split("T")[0];

    // Ensure we can create the Nexus filter
    const nexusFilterResponse = await api.post<NexusFilterResponse>(
      API_PATHS.createOrUpdateNexusFilter,
      {
        filterId: filterId,
        shouldDisable: false,
        displayName: sanitizedFilterName,
        date: sanitizedDate,
      },
    );

    if (!(nexusFilterResponse.status === 200)) {
      setPageDisplayState("Error");
      return;
    }

    if (nexusFilterResponse.data.runInProgress) {
      setPendingNexusFilterResponse(nexusFilterResponse);
      setPageDisplayState("RunInProgress");
      return;
    }

    if (nexusFilterResponse.data.dataAlreadyExists) {
      setPendingNexusFilterResponse(nexusFilterResponse);
      setPageDisplayState("AlreadyExistsWarning");
      return;
    }
    await startNexusLandingDataExtraction(nexusFilterResponse);
  };

  const handleSuccessfulAuthCheckLanding = async () => {
    const tokens = await AuthService.exchangeCodeForToken();

    if (!tokens) {
      console.error("Could not authenticate with tokens");
      navigate("/notfound");
      return;
    }

    // The landing seeds the cell as loading: a hydration that finishes after it would reset the cell
    await appInitializationService.configureAppInitialization(
      tokens.accessToken,
      tokens.refreshToken,
    );

    await initiateNexusLandingChecks();
  };

  useEffect(() => {
    // Prevent multiple executions - OAuth codes can only be used once
    if (hasInitialized.current) {
      return;
    }

    hasInitialized.current = true;

    if (hasPassedAuthChallenge()) {
      handleSuccessfulAuthCheckLanding();
    } else {
      handleInitialLanding();
    }
    // Empty dependency array - only run once on mount
  }, []);

  return (
    <div
      className="flex items-center justify-center"
      style={{ width: "100%", height: "100%" }}
    >
      {{
        Spinner: <Spinner />,
        Error: <h1>{labels.pondooNexusLandingError}</h1>,
        AlreadyExistsWarning: (
          <AlreadyExistsWarning
            isStarting={isStarting}
            onGoToExistingClick={() => {
              const data = pendingNexusFilterResponse!.data;
              navigateToCell(data.filter.id, getSanitizedStateDate()!);
            }}
            onOverwriteExistingClick={() => {
              startNexusLandingDataExtraction(pendingNexusFilterResponse!);
            }}
          />
        ),
        RunInProgress: (
          <RunInProgressDialog
            runInProgress={runInProgress!}
            stage={liveRunStage === "OPTIMIZING" ? "OPTIMIZING" : "FETCHING"}
            filterName={decodeURIComponent(
              decodeStateParam()?.filterName ?? "",
            )}
            date={getSanitizedStateDate() ?? ""}
            isStarting={isStarting}
            onFollowClick={() => {
              const { filter } = pendingNexusFilterResponse!.data;
              const date = getSanitizedStateDate()!;
              seedPlanViewStateLoading(filter.id, date);
              navigateToCell(filter.id, date);
            }}
            onStopAndRestartClick={() => {
              startNexusLandingDataExtraction(pendingNexusFilterResponse!);
            }}
          />
        ),
      }[pageDisplayState] || <Spinner />}
    </div>
  );
};

export default NexusLandingPage;
